/**
 * Sources de données de l'écran Etiketten.
 *
 * Le composant ne fait AUCUN appel réseau lui-même : il reçoit une fonction
 * `fetchResidents` en prop. Ce fichier fournit l'implémentation par défaut, qui
 * lit les commandes du point de collecte, mais n'importe quelle fonction
 * respectant le contrat ci-dessous fait l'affaire — un autre endpoint, un
 * selector Redux, un tableau en dur dans un test.
 *
 * CONTRAT
 *   fetchResidents({ signal }) → Promise<Array<{
 *     woshId: string,      // obligatoire, encodé dans le QR
 *     firstName?: string,
 *     lastName: string,
 *     room?: string|number,
 *   }>>
 *
 * Les étiquettes ne portent jamais de montant : elles servent uniquement à
 * identifier le linge d'une personne. Aucun champ de prix ici, volontairement.
 *
 * Le champ `room` reste brut : c'est le composant qui le préfixe avec un
 * libellé traduit, pour que cette couche reste indépendante d'i18next.
 */

import { fetchAllOrders, fetchResidents } from "../../../services/managerApi";

/**
 * Découpe le nom complet en prénom / nom.
 *
 * L'API ne renvoie qu'un `user_full_name` : le découpage est une convention, pas
 * une donnée. Premier mot = prénom, le reste = nom (« Jean Van Damme » → Jean /
 * Van Damme). Si le backend livre un jour des champs séparés, c'est le seul
 * endroit à changer.
 */
export const splitFullName = (fullName = "") => {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: "", lastName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

/**
 * Agrège les commandes par résident.
 *
 * Même limite assumée qu'ailleurs dans l'espace manager : il n'existe pas
 * d'endpoint « résidents », la liste est déduite des commandes. Un résident sans
 * aucune commande n'apparaît donc pas — il reste ajoutable à la main.
 */
export const residentsFromOrders = (orders = []) => {
  const map = new Map();

  orders.forEach((order) => {
    const key = order.residentId ?? order.residentName;
    if (!key) return;

    const entry = map.get(key) || {
      woshId: order.woshUserId ?? null,
      name: order.residentName,
      room: order.room,
    };

    if (!entry.woshId && order.woshUserId) entry.woshId = order.woshUserId;

    map.set(key, entry);
  });

  return [...map.values()]
    // Sans WOSH-ID il n'y a pas de QR exploitable : mieux vaut écarter la ligne
    // que d'imprimer une étiquette avec un code vide.
    .filter((entry) => entry.woshId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((entry) => {
      const { firstName, lastName } = splitFullName(entry.name);
      return {
        woshId: entry.woshId,
        firstName,
        lastName,
        room: entry.room,
      };
    });
};

/**
 * Source par défaut : les résidents du point de collecte du manager connecté.
 *
 * @param {object} authUser utilisateur du store, porteur des en-têtes d'auth
 * @returns {(args: {signal?: AbortSignal}) => Promise<Array>}
 */
export const createOrdersSource = (authUser) => async ({ signal } = {}) => {
  // Même paire que la liste des résidents : l'annuaire dit qui existe, les
  // commandes complètent. Sans l'annuaire, un résident sans commande était
  // introuvable ici — donc celui qu'on vient de créer pour lui imprimer son
  // premier étiquetage, ce qui est précisément le parcours attendu.
  // fetchAllOrders renvoie { orders, truncated, totalPages }, pas un tableau.
  const [{ orders }, roster] = await Promise.all([
    fetchAllOrders({ authUser, signal }),
    fetchResidents({ authUser, signal }).catch(() => []),
  ]);

  return mergeDirectory(residentsFromOrders(orders), roster);
};

/**
 * Ajoute les résidents de l'annuaire absents des commandes.
 *
 * Sans identifiant WOSH il n'y a pas de QR imprimable : ces entrées restent
 * écartées, ici comme pour celles issues des commandes.
 */
export const mergeDirectory = (fromOrders, roster = []) => {
  const known = new Set(fromOrders.map((entry) => String(entry.woshId)));

  const extra = (roster || [])
    .filter((resident) => resident.woshUserId && !known.has(String(resident.woshUserId)))
    .map((resident) => {
      const { firstName, lastName } = splitFullName(resident.name || "");
      return {
        woshId: resident.woshUserId,
        firstName: resident.firstName || firstName,
        lastName: resident.lastName || lastName,
        room: null,
      };
    });

  return [...fromOrders, ...extra].sort((a, b) =>
    `${a.lastName} ${a.firstName}`.trim().localeCompare(`${b.lastName} ${b.firstName}`.trim())
  );
};
